import { calendarDate } from '../dates';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { graphql } from '../api/client';
import { coverageCount, coveredStates, noNoticeStates, useCoverage, type Coverage, type CoverageNotice, type ProjectType } from '../api/coverage';
import { legalDocuments, type LegalKind } from '../content/legal';
import { findState, usStates, type UsState } from '../content/states';

/** The steps of a notice, as the product runs them; the home page and How it works both show these. */
const workflowSteps = [
  { title: 'Add the job', body: 'Enter the job, who hired you and what you supplied, or start from your contract and PayRight fills in what it finds. Import a spreadsheet for many jobs at once.' },
  { title: 'See when it is due', body: 'PayRight dates the notice from the state’s rule and shows the job facts behind the date.' },
  { title: 'Confirm who it goes to', body: 'PayRight searches public records for the people the notice must reach and shows where it found each one. You confirm or correct them, then sign.' },
  { title: 'Send it your way', body: 'Mail it yourself by certified mail, have PayRight mail it for $15 a letter, or, for a Pennsylvania notice, have PayRight file it in the state directory.' },
  { title: 'Keep the proof', body: 'The notice as prepared, each certified number, the letters as mailed and any filing confirmation stay with the notice.' }
];

/** Who a notice can have to reach, in the words the confirmation screen uses (`NoticeRecipientsConfirmation`). */
const recipientRoles = [
  ['Owner', 'The property’s owner, as the public record shows.'],
  ['Contractor', 'The company that contracted with the owner for the project.'],
  ['Lender', 'The bank or lender financing the project, if there is one.'],
  ['Surety', 'The company that issued the payment bond, if there is one.'],
  ['Public authority', 'The government body the project is for.'],
  ['Owner’s designee', 'Someone the owner named to receive notices on the owner’s behalf.']
] as const;


const kindLabels: Record<string, string> = { PRELIMINARY_NOTICE: 'Preliminary notice', FOLLOW_UP_NOTICE: 'Follow-up notice' };
const kindOrder = Object.keys(kindLabels);
const numberWords = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const byKind = (a: string, b: string) => (kindOrder.indexOf(a) + 1 || 99) - (kindOrder.indexOf(b) + 1 || 99) || a.localeCompare(b);
const kindLabel = (kind: string) => kindLabels[kind] ?? kind.toLowerCase().replaceAll('_', ' ').replace(/^./, letter => letter.toUpperCase());
const projectTypeLabels = { PRIVATE: 'private', PUBLIC: 'state and local public', FEDERAL: 'federal' } as const;
const listed = (items: string[], joiner = 'and') => items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} ${joiner} ${items.at(-1)}`;
/** Named so visitors know what is coming; nothing here is sold or described until it exists. */
const plannedServices = ['notices of intent', 'mechanics liens', 'bond and Miller Act claims', 'lien waivers and releases'];
const plannedSentence = `${listed(plannedServices).replace(/^./, letter => letter.toUpperCase())}.`;

/** "Preliminary and follow-up notices": the kinds of notice a state's rules produce. */
const kindsOf = (notices: CoverageNotice[]) => {
  const kinds = [...new Set(notices.map(notice => notice.kind))].sort(byKind);
  return kinds.length === 1 ? kindLabel(kinds[0]!) : `${listed(kinds.map(kind => kindLabel(kind).replace(/ notice$/, '').toLowerCase()))} notices`.replace(/^./, letter => letter.toUpperCase());
};

/**
 * Where a state's live rules find no preliminary notice needed, in the customer's words: the kinds of job where no notice
 * PayRight prepares applies to anyone ("private jobs"), and those where it does for some claimants but not others.
 */
function noNoticeKinds(types: ProjectType[], notices: CoverageNotice[]) {
  const prepared = new Set(notices.flatMap(notice => notice.projectTypes));
  const labels = (filter: (type: ProjectType) => boolean) => types.filter(filter).map(type => projectTypeLabels[type]);
  return { only: labels(type => !prepared.has(type)), some: labels(type => prepared.has(type)) };
}

/** "No preliminary notice required on private jobs": a state card's line for its no-notice rules, or null without any. */
function noNoticeLine(coverage: Coverage, code: string) {
  const types = coverage.noNotice.get(code);
  if (!types) return null;
  const { only, some } = noNoticeKinds(types, coverage.states.get(code) ?? []);
  return [only.length ? `No preliminary notice required on ${listed(only, 'or')} jobs` : '',
    some.length ? `${only.length ? 'and for some claimants' : 'No preliminary notice required for some claimants'} on ${listed(some, 'or')} jobs` : ''].filter(Boolean).join(', ');
}

/** What a state page says of its no-notice rules: PayRight's reading of the law, and that it says so per job, with the law. */
function noNoticeReading(place: UsState, types: ProjectType[], notices: CoverageNotice[]) {
  const { only, some } = noNoticeKinds(types, notices);
  const findings = [only.length ? `no preliminary notice is required on ${listed(only, 'or')} jobs` : '', some.length ? `some claimants need send no preliminary notice on ${listed(some, 'or')} jobs` : ''].filter(Boolean);
  return `PayRight’s reading of ${place.name} law is that ${findings.join(', and ')}. When you add a job, PayRight checks it and tells you, citing the law.`;
}

/** An excerpt of the screen where a customer confirms who a notice goes to, with example names. */
function RecipientsExcerpt() {
  const found = [
    { role: 'Owner', name: 'Bayside Medical Properties LLC', address: '4120 Harbor Blvd, Tampa, FL 33611', source: 'Found at the county property appraiser’s site (view source).', usps: true },
    { role: 'Contractor', name: 'Coastline Builders, Inc.', address: '880 Channelside Dr, Tampa, FL 33602', source: 'Found on the recorded notice of commencement (view source).', usps: true },
    { role: 'Lender', name: 'Gulf Harbor Bank', address: '200 N Ashley Dr, Tampa, FL 33602', source: 'Found on the recorded notice of commencement (view source).', usps: true }
  ];
  return <figure className="excerpt" aria-label="Example: confirming who a notice goes to">
    <div className="excerpt-bar"><span>Example job</span><span>Florida notice to owner</span></div>
    <div className="excerpt-body">
      <p className="excerpt-title">Confirm who this notice goes to</p>
      {found.map(item => <div className="excerpt-row" key={item.role}>
        <span className="excerpt-role">{item.role}</span>
        <strong>{item.name}</strong>
        <span>{item.address}</span>
        <small>{item.source} {item.usps && 'USPS recognizes this address.'}</small>
      </div>)}
      <div className="excerpt-row excerpt-missing">
        <span className="excerpt-role">Surety</span>
        <small>A payment bond is rarely online. Ask the general contractor or the government body for a copy.</small>
      </div>
      <div className="excerpt-sign"><span>Your signature</span><span className="excerpt-button">Confirm and sign</span></div>
    </div>
    <figcaption>A simplified example of the screen where you confirm recipients. Names, addresses and sources are made up.</figcaption>
  </figure>;
}

/** What every page that shows coverage says about the law: PayRight reads it, cites it, and does not advise on it. */
function LegalStatement() {
  return <p className="legal-statement">PayRight prepares each notice from the state’s statute and cites the law it follows. PayRight is not a law firm, and nothing here is legal advice.</p>;
}

/**
 * The fifty states and DC as tiles, each linking to its page: filled where PayRight has a live notice rule, outlined where
 * its live rules find no preliminary notice needed, and dashed where it has no rule.
 */
function CoverageMap({ coverage, compact = false }: { coverage: Coverage; compact?: boolean }) {
  return <div className={compact ? 'tile-map compact' : 'tile-map'}>
    <nav className="tile-grid" aria-label="States">
      {usStates.map(state => {
        const [style, said] = coverage.states.has(state.code) ? ['tile live', 'notice rules in PayRight']
          : coverage.noNotice.has(state.code) ? ['tile no-notice', 'no preliminary notice required'] : ['tile', 'no notice rules yet'];
        return <Link key={state.code} to={`/states/${state.slug}`} className={style} style={{ gridRow: state.row + 1, gridColumn: state.col + 1 }}
          aria-label={`${state.name}: ${said}`} title={state.name}>{state.code}</Link>;
      })}
    </nav>
    <p className="tile-legend"><span><span className="tile-key live" aria-hidden="true" />Notices PayRight prepares</span>
      <span><span className="tile-key no-notice" aria-hidden="true" />No preliminary notice required</span>
      <span><span className="tile-key" aria-hidden="true" />No notice rules yet</span></p>
  </div>;
}

/** A sentence of coverage that is always read from the live rules, never written by hand. */
function coverageSentence(coverage: Coverage) {
  return `PayRight has notice rules for ${coverageCount(coverage)}${coverage.federal.length ? ', plus federal jobs under the Miller Act' : ''}.`;
}

function CoverageSection() {
  const state = useCoverage();
  return <section className="section coverage-section" aria-labelledby="coverage-title">
    <div className="coverage-text">
      <h2 id="coverage-title">Where PayRight prepares notices</h2>
      {state.status === 'ready' && <p className="coverage-lead">{coverageSentence(state.coverage)}</p>}
      {state.status === 'loading' && <p className="coverage-lead" role="status">Reading PayRight’s notice rules…</p>}
      <LegalStatement />
      <Link className="button button-quiet" to="/states">See every state</Link>
    </div>
    {state.status === 'ready' && <CoverageMap coverage={state.coverage} compact />}
  </section>;
}

function RecipientResearch() {
  return <section className="section research-section" aria-labelledby="research-title">
    <div className="research-intro">
      <h2 id="research-title">PayRight finds who the notice goes to. You confirm it.</h2>
      <p>A preliminary notice protects you only if it reaches the people the law names. PayRight searches the public records for them, and, where the state records one, reads the notice of commencement, which can name the contractor, lender, surety and others.</p>
      <ul className="research-points">
        <li><strong>It shows its sources.</strong> Each suggestion names the website it came from, with a link to see the record yourself.</li>
        <li><strong>It checks the address.</strong> PayRight says whether USPS recognizes each address it found, and offers USPS’s version when it differs.</li>
        <li><strong>It says what it could not find.</strong> And where to get it, such as asking the general contractor for the payment bond.</li>
        <li><strong>You decide.</strong> You confirm or correct each recipient, add anyone missing, then sign. The notice goes only to the people you confirm.</li>
      </ul>
    </div>
    <dl className="role-ledger">
      {recipientRoles.map(([role, meaning]) => <div key={role}><dt>{role}</dt><dd>{meaning}</dd></div>)}
      <div className="role-ledger-note"><dd>Which of these a notice must reach depends on the state, the job and who hired you. PayRight asks only for the ones the rule names.</dd></div>
    </dl>
  </section>;
}

function Steps() {
  return <ol className="step-row">
    {workflowSteps.map((step, index) => <li key={step.title}><span className="step-number">{index + 1}</span><h3>{step.title}</h3><p>{step.body}</p></li>)}
  </ol>;
}

function StartBand({ title }: { title: string }) {
  return <section className="section cta">
    <div><h2>{title}</h2><p>The free plan has no monthly fee: you pay $39 for each notice you send.</p></div>
    <div className="cta-actions"><Link className="button" to="/signup">Start free</Link><Link className="button button-quiet" to="/pricing">See pricing</Link></div>
  </section>;
}

export function HomePage() {
  return <>
    <section className="hero">
      <div className="hero-text">
        <p className="kicker">For construction credit teams at subcontractors and material suppliers</p>
        <h1>Send each notice to the right people, on time.</h1>
        <p className="hero-copy">PayRight finds who each preliminary notice must reach in public records, shows you where it found them, and has you confirm every recipient before you sign. Then it dates the notice, sends it the way you choose and keeps the proof.</p>
        <div className="hero-actions"><Link className="button" to="/signup">Start free</Link><Link className="button button-quiet" to="/how-it-works">See how it works</Link></div>
      </div>
      <div className="hero-visual"><RecipientsExcerpt /></div>
    </section>
    <RecipientResearch />
    <section className="section steps-section" aria-labelledby="steps-title">
      <h2 id="steps-title">From a new job to proof of delivery</h2>
      <Steps />
    </section>
    <CoverageSection />
    <section className="section dark-panel"><div><h2>Less deadline chasing. More confidence on every job.</h2></div><div className="stat-grid"><div><strong>Credit and AR teams</strong><span>See which jobs need a notice, and which are waiting on missing details.</span></div><div><strong>Material suppliers</strong><span>Import many jobs at once and keep every notice and delivery record together.</span></div><div><strong>Subcontractors</strong><span>Start from your contract, and keep notice work and proof organized from first furnishing on.</span></div><div><strong>Owners and branch leaders</strong><span>See the workload and what was sent, without digging through spreadsheets.</span></div></div></section>
    <StartBand title="Start with your next preliminary notice." />
  </>;
}

export function HowItWorksPage() {
  return <>
    <section className="page-hero"><h1>From a new job to proof of delivery.</h1><p>PayRight brings your job details, notice dates, recipients and delivery proof into one process. Legal questions still go to your own licensed counsel.</p></section>
    <section className="section steps-section" aria-labelledby="steps-title"><h2 id="steps-title">Five steps for every notice</h2><Steps /></section>
    <RecipientResearch />
    <section className="section split-duties" aria-labelledby="duties-title">
      <h2 id="duties-title">What PayRight does, and what you do</h2>
      <div className="duty-columns">
        <div><h3>PayRight</h3><ul>
          <li>Reads your contract, if you start from one, and quotes the line each detail came from.</li>
          <li>Dates each notice from the state’s rule, and says when to mail it by where that is earlier.</li>
          <li>Researches recipients in public records and checks their addresses with USPS.</li>
          <li>Prepares the notice, ready for you to read and sign.</li>
          <li>Mails it by certified mail with return receipt, or files a Pennsylvania notice, when you choose.</li>
          <li>Emails reminders before a notice date, on the Protect and Scale plans.</li>
        </ul></div>
        <div><h3>You</h3><ul>
          <li>Give the job’s details, and correct them when they change.</li>
          <li>Confirm each recipient, and add anyone PayRight could not find.</li>
          <li>Sign the notice, or sign it before a notary where the law requires it.</li>
          <li>Choose how it is delivered, and record it when you deliver it yourself.</li>
        </ul></div>
      </div>
    </section>
    <CoverageSection />
    <section className="section planned" id="planned"><h2>Planned, not available yet</h2><p>{plannedSentence} <Link className="text-link" to="/contact">Tell us which you need</Link>.</p></section>
    <StartBand title="Start with your next preliminary notice." />
  </>;
}

const pageContent: Record<string, { title: string; intro: string; sections: Array<{ title: string; body: string; path?: string; link?: string }> }> = {
  '/preliminary-notices': { title: 'Get notice work out of the spreadsheet.', intro: 'A preliminary notice tells the owner and others on a job that you supplied work or materials. PayRight takes each one from job details to proof of delivery, in every state where it has a notice rule.', sections: [
    { title: 'Know who it goes to', body: 'PayRight looks up the owner and others the notice must reach in public records and shows where it found each. You confirm or correct each recipient before anything is sent.' },
    { title: 'See the date and why', body: 'Each date shows the job details and rule behind it.', path: '/states', link: 'See where PayRight has rules' },
    { title: 'Send it and keep the proof', body: 'Mail it yourself and record the certified numbers, have PayRight mail it by certified mail with return receipt, or have PayRight file a Pennsylvania notice. Every record stays with the notice.', path: '/pricing', link: 'See what each costs' }
  ] }
};

export function ContentPage({ path }: { path: string }) {
  const content = pageContent[path];
  if (!content) return <NotFoundPage />;
  return <><section className="page-hero"><h1>{content.title}</h1><p>{content.intro}</p></section><section className="section card-grid">{content.sections.map((section) => <article className="content-card" key={section.title}><h2>{section.title}</h2><p>{section.body}</p>{section.path && <Link className="text-link" to={section.path}>{section.link}</Link>}</article>)}</section></>;
}

/** Plans and prices as `apps/api/src/billing/policy.ts` sets them. */
export function PricingPage() {
  const plans: Array<{ name: string; price: string; per?: string; points: string[]; cta: string; path: string }> = [
    { name: 'Free', price: '$0', points: ['One user', 'Pay for each notice as you send it'], cta: 'Start free', path: '/signup' },
    { name: 'Protect', price: '$99', per: 'a month', points: ['Up to three users', 'Two notices included each month', 'Email reminders before notice dates'], cta: 'Start free, then choose Protect', path: '/signup' },
    { name: 'Scale', price: '$299', per: 'a month', points: ['Up to ten users', 'Eight notices included each month', 'Email reminders before notice dates'], cta: 'Start free, then choose Scale', path: '/signup' },
    { name: 'Enterprise', price: 'By quote', points: ['More than ten users', 'Set up with us rather than online'], cta: 'Talk to us', path: '/contact' }
  ];
  const prices: Array<[string, string]> = [
    ['Preliminary or follow-up notice', '$39, or one included notice'],
    ['You mail it yourself by certified mail', 'No PayRight charge; you pay USPS'],
    ['PayRight mails it by certified mail with return receipt', '$15 per letter, one per recipient'],
    ['PayRight files a Pennsylvania notice in the state directory', 'No charge'],
  ];
  return <>
    <section className="page-hero"><h1>A monthly price, and a price for each notice.</h1><p>Each notice has one price, charged ahead of its notice date. If PayRight mails it, the letters are paid for before any goes out.</p></section>
    <section className="section pricing-grid">{plans.map(plan => <article key={plan.name} className="price-card">
      <h2>{plan.name}</h2><p className="price">{plan.price}{plan.per && <span> {plan.per}</span>}</p>
      <ul>{plan.points.map(point => <li key={point}>{point}</li>)}</ul>
      <Link className="button button-quiet" to={plan.path}>{plan.cta}</Link>
    </article>)}</section>
    <section className="section pricing-notes">
      <div><h2>What each notice costs</h2><div className="price-list">{prices.map(([item, price]) => <div key={item}><span>{item}</span><strong>{price}</strong></div>)}</div></div>
      <div><h2>On every plan</h2><ul className="check-list"><li>Jobs, contract reading and spreadsheet import</li><li>Recipient research in public records, with sources</li><li>Notice dates with the facts behind them</li><li>Proof and files kept with each notice</li><li>The job summary and its spreadsheet download</li></ul><p className="muted">Included notices are for the month they come with and do not carry over. Planned, not available yet: {listed(plannedServices)}.</p></div>
    </section>
  </>;
}

function noticeDetails(notice: CoverageNotice): string[] {
  const types = notice.projectTypes.map(type => projectTypeLabels[type]);
  return [
    ...(types.length ? [`For ${listed(types)} jobs.`] : []),
    ...(notice.directoryFiling ? ['Filed in the state’s online notice directory; PayRight can file it for you.'] : []),
    ...(notice.notarized ? ['Sworn before a notary, so you sign it in front of one and deliver it yourself.'] : [])
  ];
}

function NoticeList({ notices }: { notices: CoverageNotice[] }) {
  return <ul className="notice-list">{[...notices].sort((a, b) => byKind(a.kind, b.kind)).map(notice => <li key={`${notice.kind}-${notice.title ?? ''}`}>
    {notice.title && <div className="notice-head"><span className="notice-kind">{kindLabel(notice.kind)}</span></div>}
    <h3>{notice.title ? notice.title.replace(/^./, letter => letter.toUpperCase()) : kindLabel(notice.kind)}</h3>
    {noticeDetails(notice).map(detail => <p key={detail}>{detail}</p>)}
    {notice.citation && <p className="citation">Law: <a href={notice.citation.url} target="_blank" rel="noreferrer">{notice.citation.label}</a></p>}
  </li>)}</ul>;
}

function CoverageUnavailable() {
  return <section className="section"><div className="alert" role="alert">PayRight could not read its notice rules just now. Reload the page to try again, or <Link className="text-link" to="/contact">ask us</Link> about a state.</div></section>;
}

function Loading() {
  return <section className="section"><p role="status" className="muted">Reading PayRight’s notice rules…</p></section>;
}

export function StatesPage() {
  const state = useCoverage();
  const coverage = state.status === 'ready' ? state.coverage : null;
  const uncovered = coverage ? usStates.filter(item => !coverage.states.has(item.code) && !coverage.noNotice.has(item.code)) : [];
  const unneeded = coverage ? noNoticeStates(coverage) : [];
  return <>
    <section className="page-hero"><h1>Where PayRight prepares notices.</h1>
      <p>{coverage ? `${coverageSentence(coverage)} This page is read from PayRight’s live notice rules, so it changes when they do.` : 'This page is read from PayRight’s live notice rules, so it changes when they do.'}</p>
      <LegalStatement />
    </section>
    {state.status === 'loading' && <Loading />}
    {state.status === 'failed' && <CoverageUnavailable />}
    {coverage && <>
      <section className="section states-map-section">
        <CoverageMap coverage={coverage} />
        <div className="map-aside">
          {unneeded.length > 0 && <div><h2>No preliminary notice required</h2><p>PayRight’s reading of the law in {listed(unneeded.map(item => item.name))} is that no preliminary notice is required on the jobs its rules there cover. When you add a job, PayRight checks it and tells you, citing the law. <a className="text-link" href="#no-notice-title">See which jobs</a>.</p></div>}
          {uncovered.length > 0 && <div><h2>No notice rules yet</h2><p>PayRight cannot prepare a state notice for a job in {listed(uncovered.map(item => item.name))} today. <Link className="text-link" to="/contact">Tell us if you need one</Link>.</p></div>}
          {coverage.federal.length > 0 && <div><h2>Federal jobs</h2><p>Jobs on federal property follow federal law, the Miller Act, in every state, so one set of rules covers them wherever they are. <a className="text-link" href="#federal">See the federal notices</a>.</p></div>}
        </div>
      </section>
      <section className="section compact-section" aria-labelledby="state-list-title">
        <h2 id="state-list-title">States with notice rules</h2>
        <p className="section-note">A state listed here may still lack a rule for your kind of job or your place in it. If no rule fits, your notice waits with no date until there is one, and PayRight emails you.</p>
        <ul className="state-list">{coveredStates(coverage).map(item => <li key={item.code}><Link to={`/states/${item.slug}`}><strong>{item.name}</strong>
          <span>{kindsOf(coverage.states.get(item.code)!)}</span>{noNoticeLine(coverage, item.code) && <span>{noNoticeLine(coverage, item.code)}</span>}</Link></li>)}</ul>
      </section>
      {unneeded.length > 0 && <section className="section compact-section" aria-labelledby="no-notice-title">
        <h2 id="no-notice-title">States where no preliminary notice is required</h2>
        <p className="section-note">PayRight’s rules for these states find that the law asks no preliminary notice on the kinds of job below. When you add a job, PayRight checks it against them and tells you, citing the law.</p>
        <ul className="state-list">{unneeded.map(item => <li key={item.code}><Link to={`/states/${item.slug}`}><strong>{item.name}</strong><span>{noNoticeLine(coverage, item.code)}</span></Link></li>)}</ul>
      </section>}
      {coverage.federal.length > 0 && <section className="section compact-section" id="federal" aria-labelledby="federal-title">
        <h2 id="federal-title">Federal jobs</h2>
        <NoticeList notices={coverage.federal} />
      </section>}
    </>}
  </>;
}

export function StatePage() {
  const { state: param = '' } = useParams();
  const place = findState(param);
  if (!place) return <NotFoundPage />;
  if (param !== place.slug) return <Navigate to={`/states/${place.slug}`} replace />;
  return <StateOverview place={place} />;
}

function StateOverview({ place }: { place: UsState }) {
  const state = useCoverage();
  const coverage = state.status === 'ready' ? state.coverage : null;
  const notices = coverage?.states.get(place.code) ?? [];
  const unneeded = coverage?.noNotice.get(place.code);
  const served = notices.length > 0 || !!unneeded;
  const federal: ReactNode = coverage?.federal.length ? <p className="section-note">Jobs on federal property in {place.name} follow the Miller Act, for which PayRight also has rules. <Link className="text-link" to="/states#federal">See federal jobs</Link>.</p> : null;
  return <>
    <section className="page-hero">
      <p className="kicker"><Link className="text-link" to="/states">All states</Link></p>
      <h1>{place.name} construction notices</h1>
      {coverage && notices.length > 0 && <p>PayRight prepares {notices.length === 1 ? 'this notice' : `these ${numberWords[notices.length] ?? notices.length} notices`} for {place.name} jobs, from job details to proof of delivery.</p>}
      {coverage && unneeded && <p>{noNoticeReading(place, unneeded, notices)}</p>}
      {coverage && !served && <p>PayRight has no notice rules for {place.name} yet, so it cannot prepare a {place.name} notice today.</p>}
      {coverage && <LegalStatement />}
      {coverage && !notices.length && federal}
      {coverage && (served ? <Link className="button" to="/signup">Start free</Link> : <Link className="button" to="/contact">Tell us you need {place.name}</Link>)}
    </section>
    {state.status === 'loading' && <Loading />}
    {state.status === 'failed' && <CoverageUnavailable />}
    {coverage && notices.length > 0 && <section className="section" aria-labelledby="notices-title">
      <h2 id="notices-title">Notices for {place.name} jobs</h2>
      <p className="section-note">Which notice a job needs depends on the job and who hired you. PayRight asks for what decides it when you add the job.</p>
      <NoticeList notices={notices} />
      {federal}
    </section>}
    {coverage && notices.length > 0 && <section className="section steps-section" aria-labelledby="state-steps-title"><h2 id="state-steps-title">How a {place.name} notice works in PayRight</h2><Steps /></section>}
  </>;
}

function DateCalculatorWidget() {
  const [anchor, setAnchor] = useState('');
  const [days, setDays] = useState('45');
  const result = useMemo(() => {
    if (!anchor || !/^\d+$/.test(days)) return null;
    const date = new Date(`${anchor}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + Number(days));
    return calendarDate(`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`, 'long');
  }, [anchor, days]);
  return <div className="calculator-panel"><form onSubmit={(event) => event.preventDefault()}><label>Starting date<input type="date" value={anchor} onChange={(event) => setAnchor(event.target.value)} /></label><label>Number of calendar days<input inputMode="numeric" value={days} onChange={(event) => setDays(event.target.value)} /></label></form><div className="calculator-result" aria-live="polite"><span>Example date</span><strong>{result ?? 'Choose a starting date'}</strong></div></div>;
}

export function CalculatorPage() {
  return <><section className="page-hero"><h1>See what date falls a set number of days later.</h1><p>This tool only adds calendar days to a date. It does not choose a legal rule, adjust for holidays, or determine a real deadline.</p></section><section className="section"><DateCalculatorWidget /></section></>;
}

export function ContactPage() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSending(true);
    setError('');
    try {
      await graphql({
        query: 'mutation Contact($name:String!,$email:String!,$company:String!,$volume:String!,$message:String!){submitContactRequest(name:$name,email:$email,company:$company,volume:$volume,message:$message)}',
        variables: { name: form.get('name'), email: form.get('email'), company: form.get('company'), volume: form.get('volume'), message: form.get('message') }
      });
      setSent(true);
    } catch (reason) {
      setError((reason as Error).message || 'We could not send that. Please try again.');
    } finally {
      setSending(false);
    }
  }
  return <><section className="page-hero"><h1>What makes notice work harder than it should be?</h1><p>Tell us about your team, how many new jobs you take on, and how you handle notices today.</p></section><section className="section form-panel">{sent ? <div role="status"><h2>Thanks — we'll be in touch.</h2></div> : <form onSubmit={submit}><label>Name<input required name="name" autoComplete="name" maxLength={200} /></label><label>Work email<input required type="email" name="email" autoComplete="email" maxLength={320} /></label><label>Company<input required name="company" autoComplete="organization" maxLength={200} /></label><label>New projects each month<select name="volume"><option>1–4</option><option>5–20</option><option>21–100</option><option>100+</option></select></label><label className="full-field">What should be easier?<textarea required name="message" rows={5} maxLength={8000} /></label>{error && <div className="alert error" role="alert">{error}</div>}<button className="button" type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send'}</button></form>}</section></>;
}

export function LegalPage({ kind }: { kind: LegalKind }) {
  const document = legalDocuments[kind];
  return <section className="legal-page">
    <p className="kicker">Effective {document.effectiveDate}</p>
    <h1>{document.title}</h1>
    <p className="legal-intro">{document.intro}</p>
    {document.sections.map((section) => <section className="legal-section" key={section.heading}>
      <h2>{section.heading}</h2>
      {section.body && <p>{section.body}</p>}
      {section.points && <ul>{section.points.map((point) => <li key={point}>{point}</li>)}</ul>}
    </section>)}
  </section>;
}

export function NotFoundPage() { return <section className="page-hero"><p className="kicker">Page not found</p><h1>That page is not here.</h1><p>It may have moved. Start from the home page, or see <Link className="text-link" to="/states">where PayRight prepares notices</Link>.</p><Link className="button" to="/">Return home</Link></section>; }
